import { useEffect, useRef } from "react";

/**
 * Canvas particle "lightning bolt" — a charged particle network shaped
 * roughly like a vertical lightning glyph, with subtle arcs and a glowing core.
 */
export default function LightningCanvas() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const boltPath = [
      [0.55, 0.06],
      [0.42, 0.32],
      [0.5, 0.36],
      [0.34, 0.62],
      [0.46, 0.66],
      [0.28, 0.95],
    ];

    const ambient = Array.from({ length: 70 }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0005,
      vy: (Math.random() - 0.5) * 0.0005,
      r: Math.random() * 1.4 + 0.2,
      a: Math.random() * 0.5 + 0.2,
    }));

    const PARTICLES_PER_SEG = 36;
    const bolt = [];
    for (let i = 0; i < boltPath.length - 1; i++) {
      const [x1, y1] = boltPath[i];
      const [x2, y2] = boltPath[i + 1];
      for (let j = 0; j < PARTICLES_PER_SEG; j++) {
        const t = j / PARTICLES_PER_SEG;
        bolt.push({
          x: x1 + (x2 - x1) * t,
          y: y1 + (y2 - y1) * t,
          ox: x1 + (x2 - x1) * t,
          oy: y1 + (y2 - y1) * t,
          phase: Math.random() * Math.PI * 2,
          speed: 0.6 + Math.random() * 1.2,
          size: Math.random() * 1.6 + 0.6,
        });
      }
    }

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let last = performance.now();
    let arcTimer = 0;
    let arcs = [];

    const drawBoltCore = () => {
      ctx.save();
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = "rgba(0, 212, 255, 0.85)";
      ctx.shadowColor = "rgba(0, 212, 255, 0.9)";
      ctx.shadowBlur = 22;
      ctx.beginPath();
      boltPath.forEach(([px, py], i) => {
        const x = px * width;
        const y = py * height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      ctx.lineWidth = 6;
      ctx.strokeStyle = "rgba(0, 212, 255, 0.15)";
      ctx.shadowBlur = 40;
      ctx.stroke();
      ctx.restore();
    };

    const tick = (now) => {
      const dt = Math.min(40, now - last);
      last = now;
      ctx.clearRect(0, 0, width, height);

      // ambient dust
      for (const p of ambient) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
        ctx.beginPath();
        ctx.fillStyle = `rgba(180, 220, 255, ${p.a * 0.4})`;
        ctx.arc(p.x * width, p.y * height, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      drawBoltCore();

      // bolt particles (sparks)
      for (const s of bolt) {
        s.phase += dt * 0.004 * s.speed;
        const jitterX = Math.sin(s.phase) * 0.012;
        const jitterY = Math.cos(s.phase * 1.3) * 0.012;
        s.x = s.ox + jitterX;
        s.y = s.oy + jitterY;
        const x = s.x * width;
        const y = s.y * height;
        ctx.beginPath();
        ctx.fillStyle = "rgba(0, 212, 255, 0.9)";
        ctx.shadowColor = "rgba(0, 212, 255, 0.9)";
        ctx.shadowBlur = 12;
        ctx.arc(x, y, s.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // occasional arcs between random bolt particles
      arcTimer -= dt;
      if (arcTimer <= 0) {
        arcTimer = 60 + Math.random() * 220;
        const a = bolt[Math.floor(Math.random() * bolt.length)];
        const b = bolt[Math.floor(Math.random() * bolt.length)];
        arcs.push({ a, b, life: 280 });
      }
      arcs = arcs.filter((arc) => arc.life > 0);
      for (const arc of arcs) {
        arc.life -= dt;
        const alpha = Math.max(0, arc.life / 280) * 0.6;
        ctx.save();
        ctx.strokeStyle = `rgba(120, 220, 255, ${alpha})`;
        ctx.lineWidth = 0.7;
        ctx.shadowColor = "rgba(0, 212, 255, 0.6)";
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.moveTo(arc.a.x * width, arc.a.y * height);
        ctx.lineTo(arc.b.x * width, arc.b.y * height);
        ctx.stroke();
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-lightning-canvas"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  );
}
